import React, { useEffect, lazy, Suspense, useState } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  VStack,
  Heading,
  Box,
  Icon,
  Flex,
  HStack,
  Text,
  Center,
  Spinner
} from '@chakra-ui/react';
import { FaGamepad } from 'react-icons/fa';
import { GiCardRandom, GiPokerHand, GiTreasureMap, GiCardPlay, GiChest } from 'react-icons/gi';
import { FaGift, FaCalendarAlt, FaTrophy } from 'react-icons/fa';

// Import game components
import IslandMapGame from '../game/components/GameUI/IslandMapGame';
import ScratchCardsDisplay from '../game/components/ScratchCards/ScratchCardsDisplay';
// Import dedicated game components for each card type
import QuickCashGame from '../game/components/ScratchCards/Games/QuickCash/QuickCashGame';
import CashKingGame from '../game/components/ScratchCards/Games/CashKing/CashKingGame';
import JackpotMillionsGame from '../game/components/ScratchCards/Games/JackpotMillions/JackpotMillionsGame';
import MegaMillionsGame from '../game/components/ScratchCards/Games/MegaMillions/MegaMillionsGame';
import CustomGamePage from '../game/components/ScratchCards/Games/CustomGame/CustomGamePage';
import ScratchCardCreator from '../game/components/ScratchCards/ScratchCardCreator';
import GameCreationWizard from '../game/components/ScratchCards/GameCreation/GameCreationWizard';
// Import Virtual Deck System components
import { BatchDashboard, Documentation } from '../game/components/ScratchCards/Randomness';
import MainCardGame from './MainCardGame';
import GiftBoxGame from '../game/components/GameUI/GiftBoxGame';
import FortuneWheel from '../game/components/GameUI/FortuneWheel';
import SlotMachine from '../game/components/GameUI/SlotMachine';
import DailyPrizeCard from '../game/components/GameUI/DailyPrizeCard';

/**
 * ModularGamesContent Component
 * 
 * A streamlined, URL-driven navigation system for the games section
 * that eliminates lag and ensures direct navigation to the correct game tab.
 */
const ModularGamesContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  
  // Get the base path from the current URL
  const basePath = '/modular-profile/games';
  
  // Create a state variable to track the current tab
  const [activeTab, setActiveTab] = useState(currentPath);
  
  // We don't need custom event listeners anymore since we're using React Router
  // The activeTab will be updated automatically when the currentPath changes
  // in the useEffect below
  
  // Update the active tab when the current path changes
  useEffect(() => {
    setActiveTab(currentPath);
  }, [currentPath]);
  
  // Use React Router's navigate function for proper routing
  const navigateWithoutReload = (path) => {
    // Only navigate if the path is different from current path
    if (path !== window.location.pathname) {
      // Use React Router's navigate function for proper routing
      navigate(path);
    }
  };
  
  // Define the game tabs with their routes and icons - using relative paths
  const gameTabs = [
    { 
      name: 'Island Map', 
      path: `${basePath}/island-map`, 
      relativePath: 'island-map',
      icon: GiTreasureMap 
    },
    { 
      name: 'Scratch Cards', 
      path: `${basePath}/scratch-cards`, 
      relativePath: 'scratch-cards',
      icon: GiCardRandom 
    },
    { 
      name: 'Card Game', 
      path: `${basePath}/card-game`, 
      relativePath: 'card-game',
      icon: GiCardPlay 
    },
    { 
      name: 'Gift Box', 
      path: `${basePath}/gift-box`, 
      relativePath: 'gift-box',
      icon: GiChest 
    },
    { 
      name: 'Fortune Wheel', 
      path: `${basePath}/fortune-wheel`, 
      relativePath: 'fortune-wheel',
      icon: FaTrophy 
    },
    { 
      name: 'Slots', 
      path: `${basePath}/slots`, 
      relativePath: 'slots',
      icon: GiPokerHand 
    },
    { 
      name: 'Daily Bonus', 
      path: `${basePath}/daily-bonus`, 
      relativePath: 'daily-bonus',
      icon: FaCalendarAlt 
    }
  ];
  
  return (
    <VStack spacing={4} align="stretch">
      {/* Games title with joystick icon removed as requested */}
      <Box 
        p={5} 
        bg="linear-gradient(135deg, rgba(20, 30, 40, 0.85) 0%, rgba(10, 20, 30, 0.95) 100%)"
        borderRadius="xl"
        boxShadow="0 10px 30px -10px rgba(0, 230, 118, 0.3)"
        backdropFilter="blur(10px)"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '4px',
          bg: 'linear-gradient(90deg, rgba(0, 230, 118, 0.8), rgba(0, 230, 118, 0.2))',
          borderTopRadius: 'xl'
        }}
      >
        {/* Game Tabs Navigation */}
        <HStack 
          spacing={2} 
          mb={4} 
          overflowX="auto" 
          pb={2}
          css={{
            '&::-webkit-scrollbar': {
              height: '0px', // Hide scrollbar completely
              display: 'none', // Additional property to ensure scrollbar is hidden
            },
            // Apply -ms-overflow-style for IE and Edge
            '-ms-overflow-style': 'none',
            // Apply scrollbar-width for Firefox
            'scrollbar-width': 'none',
          }}
        >
          {gameTabs.map((tab) => {
            // Check if this tab is active based on the active tab state
            const isActive = activeTab.includes(tab.relativePath);
            
            return (
              <Box 
                key={tab.path}
                as="button"
                flex="1"
                minWidth="fit-content"
                onClick={() => navigateWithoutReload(tab.path)}
              >
                <Flex
                  align="center"
                  justify="center"
                  py={2}
                  px={4}
                  borderRadius="full"
                  bg={isActive ? "rgba(0, 230, 118, 0.2)" : "transparent"}
                  color={isActive ? "#00e676" : "gray.400"}
                  fontWeight={isActive ? "bold" : "normal"}
                  boxShadow={isActive ? "0 4px 12px rgba(0, 230, 118, 0.2)" : "none"}
                  transition="all 0.2s"
                  _hover={{
                    bg: "rgba(0, 230, 118, 0.1)",
                    color: "#00e676"
                  }}
                >
                  <Icon as={tab.icon} mr={2} />
                  <Text>{tab.name}</Text>
                </Flex>
              </Box>
            );
          })}
        </HStack>
        
        {/* Game Content Area */}
        <Box maxH="700px" overflow="auto">
          <Suspense fallback={<Center p={10}><Spinner size="xl" color="green.500" /></Center>}>
            {/* Conditionally render content based on activeTab */}
            {activeTab.includes('/island-map') && <IslandMapGame />}
            
            {/* For scratch cards, check if we're at the main path, creator, wizard, or a specific card */}
            {activeTab.includes('/scratch-cards') && 
              (activeTab.includes('/scratch-cards/wizard') ? 
                <GameCreationWizard /> :
                activeTab.includes('/scratch-cards/create') ? 
                  <ScratchCardCreator /> :
                  !activeTab.includes('/scratch-cards/') ? 
                    <ScratchCardsDisplay /> : 
                    (activeTab.includes('/scratch-cards/game/') ? 
                      <CustomGamePage /> :
                      activeTab.includes('/scratch-cards/quick_cash') ? 
                        <QuickCashGame /> : 
                        activeTab.includes('/scratch-cards/cash_king') ? 
                          <CashKingGame /> : 
                          activeTab.includes('/scratch-cards/jackpot_millions') ? 
                            <JackpotMillionsGame /> : 
                            activeTab.includes('/scratch-cards/mega_millions') ? 
                              <MegaMillionsGame /> : 
                              // Fallback to ScratchCardsDisplay if no specific card type is matched
                              <ScratchCardsDisplay />))
            }
            
            {activeTab.includes('/card-game') && <MainCardGame />}
            {activeTab.includes('/gift-box') && <GiftBoxGame />}
            {activeTab.includes('/fortune-wheel') && <FortuneWheel />}
            {activeTab.includes('/slots') && <SlotMachine />}
            {activeTab.includes('/daily-bonus') && <DailyPrizeCard />}
            {activeTab.includes('/batch-dashboard') && <BatchDashboard />}
            {activeTab.includes('/documentation') && <Documentation />}
            
            {/* Default to Island Map if no specific path matches */}
            {!gameTabs.some(tab => activeTab.includes(tab.relativePath)) && <IslandMapGame />}
          </Suspense>
        </Box>
      </Box>
    </VStack>
  );
};

export default ModularGamesContent;
